"use client"

import { useEffect, useRef } from "react"
import type { RefObject } from "react"

import { observeImpression, observeImpressionsWithin } from "@/platform/observability/impressions/visibility"
import type { ImpressionOptions } from "@/platform/observability/impressions/visibility"

export const useImpression = (ref: RefObject<Element | null>, serveId: string | null | undefined, { container = false }: { container?: boolean } = {}): void => {
  useEffect(() => {
    const element = ref.current
    if (!element || !serveId) return
    return observeImpression(element, serveId, { container })
  }, [ref, serveId, container])
}

export const useImpressions = (ref: RefObject<Element | null>, serveIds: readonly string[], options: ImpressionOptions = {}): void => {
  const { container = false, region = false } = options
  const registered = useRef(new Map<string, () => void>())
  const registeredOn = useRef<Element | null>(null)
  const key = serveIds.join(",")

  useEffect(() => {
    const element = ref.current
    const current = registered.current
    if (registeredOn.current !== element) {
      for (const stop of current.values()) stop()
      current.clear()
      registeredOn.current = element
    }
    if (!element) return
    const wanted = new Set(key ? key.split(",") : [])
    for (const [serveId, stop] of current) {
      if (wanted.has(serveId)) continue
      stop()
      current.delete(serveId)
    }
    for (const serveId of wanted) {
      if (!current.has(serveId)) current.set(serveId, observeImpression(element, serveId, { container, region }))
    }
  }, [ref, key, container, region])

  useEffect(() => {
    const current = registered.current
    return () => {
      for (const stop of current.values()) stop()
      current.clear()
      registeredOn.current = null
    }
  }, [])
}

export const useImpressionsWithin = (ref: RefObject<Element | null>, enabled: boolean): void => {
  useEffect(() => {
    const element = ref.current
    if (!element || !enabled) return
    return observeImpressionsWithin(element)
  }, [ref, enabled])
}
